"use client";

import { useRef, useEffect, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { CatItem } from "@/types/cat";
import { CatCard } from "./CatCard";

interface VirtualCatGridProps {
  cats: CatItem[];
  onCatClick: (cat: CatItem) => void;
}

const ROW_HEIGHT = 220; // includes card padding and gap
const GAP = 16;

function getColumnCount(width: number): number {
  if (width < 640) return 3;
  if (width < 1024) return 4;
  if (width < 1536) return 5;
  return 6;
}

export function VirtualCatGrid({ cats, onCatClick }: VirtualCatGridProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [columnCount, setColumnCount] = useState(4);

  useEffect(() => {
    const updateColumns = () => {
      if (parentRef.current) {
        setColumnCount(getColumnCount(parentRef.current.offsetWidth));
      }
    };

    updateColumns();
    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, []);

  const rowCount = Math.ceil(cats.length / columnCount);

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 3,
  });

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div
      ref={parentRef}
      className="h-[600px] overflow-auto lg:h-full"
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {virtualItems.map((virtualRow) => {
          const rowIndex = virtualRow.index;
          const startIndex = rowIndex * columnCount;
          const rowCats = cats.slice(startIndex, startIndex + columnCount);

          return (
            <div
              key={virtualRow.key}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start}px)`,
              }}
              className="grid px-1"
              data-grid-columns={columnCount}
            >
              <div
                className="grid w-full"
                style={{
                  gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
                  gap: GAP,
                }}
              >
                {rowCats.map((cat) => (
                  <CatCard key={cat.id} cat={cat} onClick={() => onCatClick(cat)} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
